import { z } from 'zod'

/**
 * The agent API contract (design 10.9). Sandbox agents, workspace agents and
 * consultants' local agents all use these shapes, so every record looks the
 * same however the work was done. Version 1.
 */
export const AGENT_API_VERSION = 'v1'

export const AgentTool = z.enum(['claude_code', 'codex', 'claude_agent_sdk', 'business_agent', 'other'])
export type AgentTool = z.infer<typeof AgentTool>

const base = {
  /** Required: a retry with the same key never creates a duplicate record. */
  idempotency_key: z.string().min(8).max(200),
  /** When the event happened on the agent's side. */
  at: z.iso.datetime({ offset: true }).optional(),
  tool: AgentTool.optional(),
}

export const AgentEvent = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('session.started'), data: z.object({ model: z.string().optional(), mode: z.enum(['sandbox', 'workspace', 'local']), session_id: z.string().optional() }) }),
  z.object({ ...base, kind: z.literal('session.ended'), data: z.object({ session_id: z.string().optional(), minutes: z.number().nonnegative().optional(), summary: z.string().max(20_000).optional(), transcript: z.string().max(2_000_000).optional() }) }),
  z.object({ ...base, kind: z.literal('progress'), data: z.object({ text: z.string().min(1).max(4000), visible_to_customer: z.boolean().default(false), step: z.string().optional() }) }),
  z.object({ ...base, kind: z.literal('question'), data: z.object({ text: z.string().min(1).max(4000), options: z.array(z.string()).max(6).optional(), confirmed_by_consultant: z.boolean().default(false) }) }),
  z.object({ ...base, kind: z.literal('note'), data: z.object({ text: z.string().min(1).max(20_000), files: z.array(z.object({ name: z.string(), mime: z.string(), content_base64: z.string().max(14_000_000) })).max(10).default([]) }) }),
  z.object({ ...base, kind: z.literal('commit'), data: z.object({ sha: z.string().min(7).max(64), branch: z.string(), message: z.string().max(4000), files_changed: z.number().int().nonnegative().optional(), insertions: z.number().int().optional(), deletions: z.number().int().optional() }) }),
  z.object({ ...base, kind: z.literal('pull_request'), data: z.object({ url: z.url(), number: z.number().int().optional(), title: z.string(), branch: z.string(), preview_url: z.url().optional() }) }),
  z.object({ ...base, kind: z.literal('test_result'), data: z.object({ suite: z.string().default('tests'), passed: z.number().int().nonnegative(), failed: z.number().int().nonnegative(), skipped: z.number().int().nonnegative().default(0), output: z.string().max(50_000).optional() }) }),
  z.object({ ...base, kind: z.literal('time'), data: z.object({ minutes: z.number().int().positive().max(24 * 60), description: z.string().max(1000).optional() }) }),
  z.object({ ...base, kind: z.literal('record_change'), data: z.object({ object: z.string(), record_id: z.uuid().optional(), changes: z.record(z.string(), z.unknown()), reason: z.string().min(1).max(1000) }) }),
  z.object({ ...base, kind: z.literal('status'), data: z.object({ status: z.enum(['ready_for_review', 'blocked', 'done', 'working']), reason: z.string().max(2000).optional() }) }),
  z.object({ ...base, kind: z.literal('tool_step'), data: z.object({ name: z.string(), summary: z.string().max(2000).optional(), ok: z.boolean().default(true) }) }),
])
export type AgentEvent = z.infer<typeof AgentEvent>
export type AgentEventKind = AgentEvent['kind']

/** What takeit4me sends back to agents over the event stream (design 10.9). */
export const OutboundAgentEvent = z.object({
  seq: z.number().int(),
  kind: z.enum(['task.assigned', 'brief.changed', 'customer.answered', 'preview.approved', 'preview.rejected', 'comment', 'task.cancelled', 'customer.message', 'checks.failed', 'ping']),
  task_id: z.string().nullable(),
  task_ref: z.string().nullable().optional(),
  data: z.record(z.string(), z.unknown()),
  created_at: z.string(),
})
export type OutboundAgentEvent = z.infer<typeof OutboundAgentEvent>

export const AgentTaskView = z.object({
  id: z.string(),
  ref: z.string(),
  title: z.string(),
  kind: z.string(),
  stage: z.string(),
  route: z.string().nullable(),
  branch: z.string().nullable(),
  brief: z.object({
    summary: z.string().default(''),
    details: z.string().default(''),
    acceptance: z.array(z.string()).default([]),
    info: z.record(z.string(), z.string()).default({}),
  }),
  customer: z.object({ name: z.string(), organisation: z.string().nullable(), summary: z.string().nullable() }),
  repo: z.string().nullable(),
  company: z.object({ name: z.string(), keep_transcripts: z.boolean(), allow_code_leave_region: z.boolean() }),
})
export type AgentTaskView = z.infer<typeof AgentTaskView>

/** Signature header for signed agent webhooks: `t=<unix seconds>,v1=<hex hmac-sha256(secret, t + "." + body)>`. */
export const SIGNATURE_HEADER = 'x-takeit4me-signature'
export const SIGNATURE_TOLERANCE_SECONDS = 300
